/**
 * Connect pillar landing (/connect index — the "Connect" bottom-nav
 * destination). Opens as a two-card chooser between the two ways to wire an AI
 * to SystemDox:
 *
 *  - Connect via SystemDox MCP — the live MCP server: the setup steps
 *    (BuildConnectPage, same component as Settings → Connect) plus a
 *    "Test SystemDox MCP" button that opens the live MCP preview panel.
 *  - Connect via Skills — export the workspace standards as committed
 *    SKILL.md files (SkillsExportSection); no server or API key.
 *
 * The chosen method rides the URL (?via=mcp | ?via=skills) so each view
 * deep-links and the browser Back button returns to the chooser. Navigation
 * merges `via` into the existing search params rather than replacing them —
 * on localhost the tenant itself rides the query string (?tenant=slug, see
 * utils/subdomain.ts) and clobbering it drops the app onto the neutral host.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  FolderDown,
  Plug,
  Terminal,
  type LucideIcon,
} from 'lucide-react';
import BuildConnectPage from './BuildConnectPage';
import { SkillsExportSection } from './SkillsExportSection';
import { McpPreviewSection } from './McpPreviewSection';

/** Current search with `via` set (or removed), as a Link target. */
function useViaLink() {
  const [searchParams] = useSearchParams();
  return (via?: string) => {
    const next = new URLSearchParams(searchParams);
    if (via) next.set('via', via);
    else next.delete('via');
    const s = next.toString();
    return { search: s ? `?${s}` : '' };
  };
}

function MethodCard({
  to,
  icon: Icon,
  gradient,
  title,
  description,
  cta,
}: {
  to: { search: string };
  icon: LucideIcon;
  gradient: string;
  title: string;
  description: string;
  cta: string;
}) {
  return (
    <Link
      to={to}
      className="group flex h-full min-h-[13rem] flex-col gap-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 text-left transition-all hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md"
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${gradient}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</div>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{description}</p>
      </div>
      <span className="mt-auto flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400">
        {cta}
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

function BackToChooser() {
  const viaLink = useViaLink();
  return (
    <Link
      to={viaLink()}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
    >
      <ArrowLeft className="w-4 h-4" />
      All connection methods
    </Link>
  );
}

/** MCP detail — the setup steps plus the "Test SystemDox MCP" reveal. Own
 *  component so the open/closed preview state resets when the view unmounts. */
function McpConnectView() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (previewOpen) previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [previewOpen]);

  return (
    <div className="space-y-8">
      <BackToChooser />

      <BuildConnectPage />

      {/* Test the connection — opens the live MCP preview panel */}
      {previewOpen ? (
        <div ref={previewRef} className="border-t border-slate-200 dark:border-slate-800 pt-8 scroll-mt-6">
          <McpPreviewSection />
        </div>
      ) : (
        <div className="flex justify-center border-t border-slate-200 dark:border-slate-800 pt-8">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors"
          >
            <Terminal className="w-4 h-4" />
            Test SystemDox MCP
          </button>
        </div>
      )}
    </div>
  );
}

function SkillsConnectView() {
  return (
    <div className="space-y-8">
      <BackToChooser />
      <SkillsExportSection />
    </div>
  );
}

export default function BuildStandardsPage() {
  const [searchParams] = useSearchParams();
  const viaLink = useViaLink();
  const via = searchParams.get('via');

  if (via === 'mcp') return <McpConnectView />;
  if (via === 'skills') return <SkillsConnectView />;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
      <MethodCard
        to={viaLink('mcp')}
        icon={Plug}
        gradient="from-blue-500 to-indigo-600"
        title="Connect via SystemDox MCP"
        description="Give Claude Code, Cursor, or any MCP-compatible tool a live connection to your checks, project rules, specs, and documentation — then test it right here with the MCP preview."
        cta="Set up MCP"
      />
      <MethodCard
        to={viaLink('skills')}
        icon={FolderDown}
        gradient="from-emerald-500 to-teal-600"
        title="Connect via Skills"
        description="Export your checks, rules, and tech stack as portable SKILL.md files. Commit them to your repo and any agent harness follows them — no MCP server or API key required."
        cta="Export Skills"
      />
    </div>
  );
}
